import { MASCOT_URL, INCIDENT_IMAGES, CATEGORY_META } from "../constants/content";

const W = 1080;
const H = 1350;

const loadImg = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const wrap = (ctx, text, maxWidth) => {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else line = test;
  }
  if (line) lines.push(line);
  return lines;
};

const stamp = (ctx, text, x, y, rot, bg, fg, font) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.font = font;
  const w = ctx.measureText(text).width + 40;
  ctx.fillStyle = bg;
  ctx.fillRect(-w / 2, -28, w, 56);
  ctx.lineWidth = 6;
  ctx.strokeStyle = "#fff";
  ctx.strokeRect(-w / 2, -28, w, 56);
  ctx.fillStyle = fg;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, 0, 2);
  ctx.restore();
};

const cautionTape = (ctx, y, h) => {
  ctx.save();
  ctx.fillStyle = "#FFEA00";
  ctx.fillRect(0, y, W, h);
  ctx.fillStyle = "#000";
  for (let x = -h; x < W + h; x += h * 2) {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + h, y);
    ctx.lineTo(x + h * 2, y + h);
    ctx.lineTo(x + h, y + h);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
};

export const generateCover = async (incident) => {
  if (document.fonts?.load) {
    await Promise.all([document.fonts.load("120px Anton"), document.fonts.load("bold 30px 'IBM Plex Mono'")]);
  }
  const [mascot, photo] = await Promise.all([loadImg(MASCOT_URL), loadImg(INCIDENT_IMAGES[incident.image_key])]);
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#0a0a0a";
  ctx.fillRect(0, 0, W, H);

  // masthead
  ctx.fillStyle = "#FF003C";
  ctx.fillRect(0, 0, W, 150);
  ctx.fillStyle = "#fff";
  ctx.font = "110px Anton";
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.fillText("HALL OF SHAME", 40, 78);
  ctx.font = "bold 24px 'IBM Plex Mono'";
  ctx.textAlign = "right";
  ctx.fillText("UNAUTHORIZED ARCHIVE", W - 40, 50);
  ctx.fillText(`${incident.date}`, W - 40, 100);
  cautionTape(ctx, 150, 26);

  // photo
  const py = 200;
  const ph = 470;
  if (photo) {
    const scale = Math.max(W / photo.width, ph / photo.height);
    const sw = W / scale;
    const sh = ph / scale;
    ctx.filter = "grayscale(1) contrast(1.25)";
    ctx.drawImage(photo, (photo.width - sw) / 2, (photo.height - sh) / 2, sw, sh, 0, py, W, ph);
    ctx.filter = "none";
  } else {
    ctx.fillStyle = "#222";
    ctx.fillRect(0, py, W, ph);
  }
  ctx.lineWidth = 8;
  ctx.strokeStyle = "#fff";
  ctx.strokeRect(0, py, W, ph);

  // mascot
  if (mascot) {
    const ms = 260;
    const mx = W - ms - 50;
    const my = py + ph - ms / 2;
    ctx.fillStyle = "#FF003C";
    ctx.fillRect(mx + 14, my + 14, ms, ms);
    ctx.fillStyle = "#000";
    ctx.fillRect(mx, my, ms, ms);
    const scale = Math.max(ms / mascot.width, ms / mascot.height);
    const sw = ms / scale;
    const sh = ms / scale;
    ctx.drawImage(mascot, (mascot.width - sw) / 2, (mascot.height - sh) / 2, sw, sh, mx, my, ms, ms);
    ctx.lineWidth = 10;
    ctx.strokeStyle = "#FFEA00";
    ctx.strokeRect(mx, my, ms, ms);
  }

  stamp(ctx, "BREAKING", 190, py + 10, -0.08, "#FFEA00", "#000", "bold 30px 'IBM Plex Mono'");
  const cat = (CATEGORY_META[incident.category] || CATEGORY_META.all).label;
  stamp(ctx, cat, 170, py + ph - 10, 0.05, "#FF00FF", "#000", "bold 24px 'IBM Plex Mono'");

  // headline
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillStyle = "#fff";
  ctx.font = "88px Anton";
  const lines = wrap(ctx, incident.title.toUpperCase(), W - 80).slice(0, 3);
  let y = py + ph + 170;
  for (const l of lines) {
    ctx.fillText(l, 40, y);
    y += 92;
  }

  // description (clamped to the space above the meter)
  ctx.font = "26px 'IBM Plex Sans', sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.8)";
  y += 16;
  const meterTop = H - 190;
  const maxLines = Math.max(0, Math.floor((meterTop - 30 - y) / 36));
  const descLines = wrap(ctx, incident.description, W - 80);
  const shown = descLines.slice(0, maxLines);
  if (descLines.length > shown.length && shown.length) shown[shown.length - 1] = shown[shown.length - 1].replace(/\s*\S*$/, "") + " …";
  for (const l of shown) {
    ctx.fillText(l, 40, y);
    y += 36;
  }

  // cringe meter
  const my2 = meterTop;
  ctx.font = "bold 24px 'IBM Plex Mono'";
  ctx.fillStyle = "#FFEA00";
  ctx.fillText("CRINGE SCALE", 40, my2);
  ctx.textAlign = "right";
  ctx.fillStyle = "#FF00FF";
  ctx.fillText(`${incident.cringe}/10`, W - 40, my2);
  ctx.textAlign = "left";
  const bx = 40;
  const by = my2 + 44;
  const bw = W - 80;
  ctx.fillStyle = "#000";
  ctx.fillRect(bx, by, bw, 44);
  ctx.fillStyle = incident.cringe >= 9 ? "#FF00FF" : incident.cringe >= 7 ? "#FF003C" : "#FFEA00";
  ctx.fillRect(bx, by, (bw * incident.cringe) / 10, 44);
  ctx.lineWidth = 6;
  ctx.strokeStyle = "#fff";
  ctx.strokeRect(bx, by, bw, 44);

  // footer
  cautionTape(ctx, H - 60, 24);
  ctx.fillStyle = "#000";
  ctx.fillRect(0, H - 36, W, 36);
  ctx.font = "bold 18px 'IBM Plex Mono'";
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.textBaseline = "middle";
  ctx.fillText(`PARODY · ${window.location.host}/?incident=${incident.id}`, 40, H - 18);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
};

export const downloadCover = async (incident) => {
  const blob = await generateCover(incident);
  if (!blob) throw new Error("cover failed");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `hall-of-shame-${incident.id}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};
